import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AssetCard from '../components/AssetCard';
import TypeBadge from '../components/TypeBadge';
import { useAuth } from '../context/AuthContext';
import api from '../api/ApiClient';
import ALL_LANGUAGES from '../config/languages.json';

const TYPES = [
  { key: null,         label: 'All'         },
  { key: 'dataset',    label: 'Datasets'    },
  { key: 'llm',        label: 'LLMs'        },
  { key: 'cv_model',   label: 'CV Models'   },
  { key: 'embedding',  label: 'Embeddings'  },
  { key: 'notebook',   label: 'Notebooks'   },
  { key: 'pipeline',   label: 'Pipelines'   },
];

const SORTS = [
  { key: 'newest',     label: 'Newest'      },
  { key: 'popular',    label: 'Most popular' },
  { key: 'rating',     label: 'Top rated'   },
  { key: 'price_asc',  label: 'Price: low'  },
  { key: 'price_desc', label: 'Price: high' },
];

function Navbar() {
  const { user, logout } = useAuth();
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 50,
      background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
      borderBottom: '1px solid var(--border)',
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 52, display: 'flex', alignItems: 'center', gap: 32 }}>
        <Link to="/" style={{ textDecoration: 'none', fontWeight: 700, fontSize: 17, color: 'var(--text)', letterSpacing: '-0.3px' }}>
          dApiary
        </Link>

        <nav style={{ display: 'flex', gap: 4, flex: 1 }}>
          {['Datasets', 'Models', 'Notebooks'].map(l => (
            <span key={l} style={{ padding: '5px 10px', borderRadius: 6, fontSize: 13, color: 'var(--text-2)', cursor: 'default' }}>{l}</span>
          ))}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {user ? (
            <>
              <Link to="/dashboard" style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, color: 'var(--text-2)', textDecoration: 'none' }}>
                Dashboard
              </Link>
              <button onClick={logout} className="btn-secondary" style={{ fontSize: 13, padding: '6px 14px' }}>Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500, color: 'var(--text-2)', textDecoration: 'none' }}>
                Sign in
              </Link>
              <Link to="/register" className="btn-primary" style={{ fontSize: 13, padding: '6px 16px', borderRadius: 'var(--radius-sm)', textDecoration: 'none' }}>
                Get started
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

// Compact language dropdown — shows only languages present in the catalog
function LangDropdown({ activeLangs, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [dbLangs, setDbLangs] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    api.getAssetFilters()
      .then(d => setDbLangs(d.languages || []))
      .catch(() => setDbLangs([]));
  }, []);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Если языки из БД загружены — показываем только их, иначе fallback на все
  const source = dbLangs.length > 0
    ? dbLangs.map(code => ALL_LANGUAGES.find(l => l.code === code) || { code, name: code.toUpperCase() })
    : ALL_LANGUAGES;

  const filtered = q
    ? source.filter(l => l.name.toLowerCase().includes(q.toLowerCase()) || l.code.toLowerCase().includes(q.toLowerCase()))
    : source;

  const count = activeLangs.length;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(!open); if (open) setQ(''); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
          border: `1px solid ${count > 0 ? '#BFDBFE' : 'var(--border)'}`,
          background: count > 0 ? 'var(--accent-bg)' : 'transparent',
          color: count > 0 ? 'var(--accent)' : 'var(--text-2)',
          fontSize: 13, fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}
      >
        Language {count > 0 && <span style={{ background: 'var(--accent)', color: '#fff', borderRadius: 10, padding: '0 5px', fontSize: 11 }}>{count}</span>}
        <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 9999, width: 230,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-lg)',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input
              autoFocus value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search languages…" className="field"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 13 }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filtered.map(l => {
              const on = activeLangs.includes(l.code);
              return (
                <div key={l.code} onClick={() => onToggle(l.code)} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                  cursor: 'pointer', fontSize: 13,
                  background: on ? 'var(--accent-bg)' : 'transparent',
                  color: on ? 'var(--accent)' : 'var(--text)',
                }}
                  onMouseOver={e => !on && (e.currentTarget.style.background = 'var(--surface2)')}
                  onMouseOut={e => !on && (e.currentTarget.style.background = 'transparent')}
                >
                  <span style={{ fontFamily: 'Space Mono', fontSize: 10, color: 'var(--text-3)', width: 28, textTransform: 'uppercase' }}>{l.code}</span>
                  <span style={{ flex: 1 }}>{l.name}</span>
                  {on && <span style={{ fontSize: 12, color: 'var(--accent)' }}>✓</span>}
                </div>
              );
            })}
          </div>
          {count > 0 && (
            <div onClick={() => { onClear(); setOpen(false); }} style={{
              padding: '8px 12px', borderTop: '1px solid var(--border)',
              fontSize: 12, color: 'var(--danger)', cursor: 'pointer',
            }}>
              Clear ({count} selected)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState(null);
  const [activeTag, setActiveTag] = useState(null);
  const [activeLangs, setActiveLangs] = useState([]);
  const [sort, setSort] = useState('newest');
  const [priceFilter, setPriceFilter] = useState('all');
  const [total, setTotal] = useState(0);

  const load = async (params = {}) => {
    setLoading(true);
    try {
      const d = await api.listAssets({ status: 'published', limit: 24, sort, ...params });
      setAssets(d.assets || []);
      setTotal((d.assets || []).length);
    } catch { setAssets([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildParams = (overrides = {}) => {
    const p = { status: 'published', limit: 24, sort, ...overrides };
    if (search && !overrides.search && !overrides.clearSearch) p.search = search;
    if (activeType && !overrides.assetType && overrides.assetType !== null) p.assetType = activeType;
    if (activeTag && !overrides.tags && overrides.tags !== null) p['tags[]'] = activeTag;
    const langs = overrides['languages[]'] !== undefined ? overrides['languages[]'] : activeLangs;
    if (langs?.length) p['languages[]'] = langs;
    if (priceFilter === 'free' && !overrides.priceMax) p.priceMax = 0;
    delete p.clearSearch;
    return p;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveTag(null);
    load(buildParams({ search: search || undefined }));
  };

  const handleType = (key) => {
    setActiveType(key);
    load(buildParams({ assetType: key || undefined }));
  };

  const handleTagClick = (tag) => {
    const next = activeTag === tag ? null : tag;
    setActiveTag(next);
    setSearch('');
    load(buildParams({ 'tags[]': next || undefined, search: undefined, clearSearch: true }));
  };

  const handleLangToggle = (code) => {
    const next = activeLangs.includes(code)
      ? activeLangs.filter(l => l !== code)
      : [...activeLangs, code];
    setActiveLangs(next);
    load(buildParams({ 'languages[]': next.length ? next : undefined }));
  };

  const clearLangs = () => {
    setActiveLangs([]);
    load(buildParams({ 'languages[]': undefined }));
  };

  const handleSort = (s) => {
    setSort(s);
    load(buildParams({ sort: s }));
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Navbar />

      {/* Hero */}
      <section style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)', padding: '56px 24px 48px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h1 style={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 14 }}>
            The marketplace for AI &amp; ML assets
          </h1>
          <p style={{ fontSize: 16, color: 'var(--text-2)', marginBottom: 32, lineHeight: 1.6 }}>
            Buy and sell high-quality datasets, language models, embeddings, notebooks, and pipelines.
          </p>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, maxWidth: 520, margin: '0 auto' }}>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search datasets, models, notebooks…"
              className="field"
              style={{ flex: 1, fontSize: 14 }}
            />
            <button type="submit" className="btn-primary" style={{ padding: '9px 22px', whiteSpace: 'nowrap' }}>Search</button>
          </form>
        </div>
      </section>

      {/* Filter bar — два ряда чтобы дропдаун не обрезался overflow */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>

          {/* Ряд 1: типы (скроллится) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, overflowX: 'auto', height: 44, scrollbarWidth: 'none' }}>
            {TYPES.map(({ key, label }) => {
              const active = activeType === key;
              return (
                <button key={String(key)} onClick={() => handleType(key)} style={{
                  padding: '5px 13px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap',
                  border: 'none', background: active ? 'var(--accent-bg)' : 'transparent',
                  color: active ? 'var(--accent)' : 'var(--text-2)',
                  fontFamily: 'inherit', transition: 'all 0.15s', flexShrink: 0,
                }}>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Ряд 2: активные фильтры + controls — overflow: visible, дропдаун выходит свободно */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8, flexWrap: 'wrap' }}>
            {/* Active tag chip */}
            {activeTag && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 6, background: 'var(--accent-bg)', border: '1px solid #BFDBFE', fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>
                #{activeTag}
                <button onClick={() => handleTagClick(activeTag)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* Active language chips */}
            {activeLangs.map(code => (
              <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 6, background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: 11, color: '#16A34A', fontWeight: 700, fontFamily: 'Space Mono' }}>
                {code.toUpperCase()}
                <button onClick={() => handleLangToggle(code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#16A34A', fontSize: 13, padding: 0, lineHeight: 1 }}>×</button>
              </div>
            ))}

            {(activeTag || activeLangs.length > 0) && (
              <button onClick={() => { setActiveTag(null); clearLangs(); }} style={{ fontSize: 12, color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', fontFamily: 'inherit' }}>
                Clear all
              </button>
            )}

            <div style={{ flex: 1 }} />

            {/* Language dropdown — здесь нет overflow, дропдаун свободно открывается вниз */}
            <LangDropdown activeLangs={activeLangs} onToggle={handleLangToggle} onClear={clearLangs} />

            <select value={sort} onChange={e => handleSort(e.target.value)} className="field" style={{ width: 'auto', fontSize: 13, padding: '5px 10px', color: 'var(--text-2)' }}>
              {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={priceFilter} onChange={e => { setPriceFilter(e.target.value); load(buildParams({ priceMax: e.target.value === 'free' ? 0 : undefined })); }} className="field" style={{ width: 'auto', fontSize: 13, padding: '5px 10px', color: 'var(--text-2)' }}>
              <option value="all">All prices</option>
              <option value="free">Free only</option>
            </select>
          </div>

        </div>
      </div>

      {/* Grid */}
      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {Array(8).fill(0).map((_, i) => (
              <div key={i} style={{ height: 300, borderRadius: 'var(--radius)', background: 'var(--surface2)', animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : assets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 24px', color: 'var(--text-2)' }}>
            <div style={{ fontSize: 40, marginBottom: 12, color: 'var(--border)' }}>—</div>
            <p style={{ fontWeight: 500 }}>No assets found</p>
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20 }}>
              {total} {activeType ? <TypeBadge type={activeType} size="sm" /> : 'assets'} found
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, alignItems: 'stretch' }}>
              {assets.map(a => <AssetCard key={a._id} asset={a} onTagClick={handleTagClick} />)}
            </div>
          </>
        )}
      </main>

      <footer style={{ borderTop: '1px solid var(--border)', padding: '24px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
        © {new Date().getFullYear()} dApiary
      </footer>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }`}</style>
    </div>
  );
}
